import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import {
    ArrowRight,
    BookOpenText,
    HeartPulse,
    Compass,
    Newspaper,
    X,
    CalendarDays,
    UserRound,
    Clock3,
    Loader2,
    Check,
    Link2,
} from "lucide-react";
import { fetchBlogs } from "../lib/blogsApi";

const FALLBACK_BLOGS = [
    {
        id: "fallback-guide",
        title: "Daily recovery tools you can actually use",
        excerpt: "Grounding prompts, journaling ideas, and check-in rituals that make hard days feel more manageable.",
        image: "https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?auto=format&fit=crop&w=1200&q=85",
        author: "Genesis Team",
        readTime: "4 min read",
        tags: ["Grounding", "Tools", "Daily Recovery"],
        details:
            "Grounding prompts, journaling ideas, and check-in rituals that make hard days feel more manageable. Small daily habits create lasting stability in recovery.",
        category: "Guide",
        type: "guides",
        date: "Oct 2026",
    },
    {
        id: "fallback-support",
        title: "What to do when cravings hit or emotions spike",
        excerpt: "Practical, compassionate guidance for moments that feel urgent, lonely, or overwhelming.",
        image: "https://images.unsplash.com/photo-1474418397713-7ede21d49118?auto=format&fit=crop&w=1200&q=85",
        author: "Genesis Team",
        readTime: "3 min read",
        tags: ["Support", "Crisis", "Urgent Care"],
        details:
            "Practical, compassionate guidance for moments that feel urgent, lonely, or overwhelming. Reach out to SAMHSA 1-800-662-4357 or 988 Suicide & Crisis Lifeline anytime.",
        category: "Support",
        type: "support",
        date: "Oct 2026",
    },
    {
        id: "fallback-1",
        title: "How to handle cravings in the first 10 minutes",
        excerpt: "Simple grounding steps you can use immediately when cravings spike.",
        image: null,
        author: "Genesis Team",
        readTime: "4 min read",
        tags: ["Grounding", "Cravings", "Daily Recovery"],
        details:
            "Cravings come in waves and typically peak and subside within 10 to 15 minutes. Taking four deep breaths, drinking a glass of cold water, using the 5-4-3-2-1 grounding technique, and reaching out to a trusted recovery friend provides immediate relief.",
        category: "Coping Skills",
        date: "Apr 2026",
    },
    {
        id: "fallback-2",
        title: "A beginner's daily check-in routine for recovery",
        excerpt: "Build a repeatable 3-minute ritual to stay connected to your progress.",
        image: null,
        author: "Hope Content Lab",
        readTime: "5 min read",
        tags: ["Check-ins", "Routine", "Consistency"],
        details:
            "Keep daily check-ins straightforward: 1) rate your mood, 2) note any craving level, and 3) choose today's focus. Following this routine consistently for two weeks helps you recognize emotional triggers and significantly reduces relapse risk.",
        category: "Daily Practice",
        date: "Apr 2026",
    },
    {
        id: "fallback-3",
        title: "Journaling prompts for difficult emotions",
        excerpt: "Try these prompts when you feel overwhelmed, anxious, or numb.",
        image: null,
        author: "Recovery Editorial",
        readTime: "6 min read",
        tags: ["Journaling", "Emotions", "Self-Reflection"],
        details:
            "Begin with prompts like: 'What am I actually feeling right now?', 'What am I afraid of in this moment?', and 'What is one healthy step I can take today?'. Writing honestly without self-judgment accelerates personal healing.",
        category: "Journaling",
        date: "Apr 2026",
    },
    {
        id: "fallback-4",
        title: "What to do after a hard day (without shame)",
        excerpt: "A gentle reset framework to help you recover emotionally and mentally.",
        category: "Mindset",
        date: "Mar 2026",
    },
    {
        id: "fallback-5",
        title: "How mentors can support your recovery plan",
        excerpt: "Learn what a healthy mentor relationship can look like in real life.",
        category: "Community",
        date: "Mar 2026",
    },
    {
        id: "fallback-6",
        title: "Privacy-first recovery tools: what to look for",
        excerpt: "A checklist for choosing apps that actually protect your personal data.",
        category: "Privacy",
        date: "Mar 2026",
    },
];

function formatBlogDate(blog) {
    if (blog?.date) return blog.date;
    if (blog?.createdDate) {
        try {
            const d = new Date(blog.createdDate);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
            }
        } catch {
            // ignore
        }
    }
    return "";
}

function slugify(text) {
    if (!text) return "";
    return String(text)
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function getBlogId(blog) {
    if (!blog) return "";
    return String(blog.id ?? blog._id ?? blog.blogId ?? slugify(blog.title) ?? "");
}

function matchBlog(blogList, query) {
    if (!query || !Array.isArray(blogList)) return null;
    const cleanQuery = decodeURIComponent(String(query)).trim().toLowerCase();
    return (
        blogList.find((b) => {
            const id = String(b?.id ?? b?._id ?? b?.blogId ?? "").toLowerCase();
            const slug = slugify(b?.title || "");
            const title = String(b?.title || "").toLowerCase();
            return id === cleanQuery || slug === cleanQuery || title === cleanQuery;
        }) || null
    );
}

function getBlogShareUrl(blog) {
    const origin =
        typeof window !== "undefined" && window.location.origin
            ? window.location.origin
            : "";
    const id = getBlogId(blog);
    return `${origin}/resources?blog=${encodeURIComponent(id)}`;
}

export default function Resources() {
    const ref = useReveal();
    const [page, setPage] = useState(1);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [blogs, setBlogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();
    const { blogId: routeBlogId } = useParams();
    const [copiedBlogId, setCopiedBlogId] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        async function loadBlogs() {
            try {
                setIsLoading(true);
                const data = await fetchBlogs({
                    pageNumber: 1,
                    pageSize: 100,
                    signal: controller.signal,
                });
                if (isMounted) {
                    if (data && Array.isArray(data.items) && data.items.length > 0) {
                        setBlogs(data.items);
                    } else {
                        setBlogs([]);
                    }
                }
            } catch (err) {
                if (isMounted && err.name !== "AbortError") {
                    console.error("Failed to fetch blogs:", err);
                    setBlogs([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        loadBlogs();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, []);

    // Only use blogs from API. If API has no data or errors, do NOT show dummy data.
    const activeBlogList = useMemo(() => {
        return blogs;
    }, [blogs]);

    // 1. Guide Blog: prioritize b.type === "guide" / "guides", then category / tags / title
    const guideBlog = useMemo(() => {
        // Priority 1: Match by explicit type
        const matchByType = activeBlogList.find((b) => {
            const type = String(b?.type || "").toLowerCase().trim();
            return type === "guide" || type === "guides" || type.includes("guide");
        });
        if (matchByType) return matchByType;

        // Priority 2: Match by category
        const matchByCategory = activeBlogList.find((b) => {
            const cat = String(b?.category || "").toLowerCase().trim();
            return cat === "guide" || cat === "guides" || cat.includes("guide");
        });
        if (matchByCategory) return matchByCategory;

        // Priority 3: Match by tags or title
        return (
            activeBlogList.find((b) => {
                if (Array.isArray(b?.tags) && b.tags.some((t) => String(t).toLowerCase().includes("guide"))) {
                    return true;
                }
                const title = String(b?.title || "").toLowerCase();
                return title.includes("guide");
            }) || null
        );
    }, [activeBlogList]);

    // 2. Support Blog: prioritize b.type === "support", then category / tags / title
    const supportBlog = useMemo(() => {
        // Priority 1: Match by explicit type
        const matchByType = activeBlogList.find((b) => {
            if (guideBlog && b.id === guideBlog.id) return false;
            const type = String(b?.type || "").toLowerCase().trim();
            return type === "support" || type.includes("support");
        });
        if (matchByType) return matchByType;

        // Priority 2: Match by category
        const matchByCategory = activeBlogList.find((b) => {
            if (guideBlog && b.id === guideBlog.id) return false;
            const cat = String(b?.category || "").toLowerCase().trim();
            return cat === "support" || cat.includes("support");
        });
        if (matchByCategory) return matchByCategory;

        // Priority 3: Match by tags or title
        return (
            activeBlogList.find((b) => {
                if (guideBlog && b.id === guideBlog.id) return false;
                if (Array.isArray(b?.tags) && b.tags.some((t) => String(t).toLowerCase().includes("support"))) {
                    return true;
                }
                const title = String(b?.title || "").toLowerCase();
                return title.includes("support") || title.includes("crisis");
            }) || null
        );
    }, [activeBlogList, guideBlog]);

    // 3. Latest Blogs: remaining blogs after guide and support are segregated
    const latestBlogs = useMemo(() => {
        return activeBlogList.filter((b) => {
            if (guideBlog && b.id === guideBlog.id) return false;
            if (supportBlog && b.id === supportBlog.id) return false;
            return true;
        });
    }, [activeBlogList, guideBlog, supportBlog]);

    const pageSize = 6;
    const totalPages = Math.max(1, Math.ceil(latestBlogs.length / pageSize));

    useEffect(() => {
        if (page > totalPages) {
            setPage(1);
        }
    }, [totalPages, page]);

    const paginatedBlogs = useMemo(() => {
        const start = (page - 1) * pageSize;
        return latestBlogs.slice(start, start + pageSize);
    }, [page, latestBlogs]);

    // Synchronize blog modal with URL (?blog=... or :blogId)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const blogParam =
            searchParams.get("blog") ||
            searchParams.get("blogId") ||
            searchParams.get("id") ||
            routeBlogId;

        if (blogParam) {
            const listToSearch =
                activeBlogList.length > 0 ? activeBlogList : FALLBACK_BLOGS;
            const matched = matchBlog(listToSearch, blogParam);
            if (matched) {
                setSelectedBlog((prev) => {
                    if (prev && getBlogId(prev) === getBlogId(matched)) {
                        return prev;
                    }
                    return matched;
                });
                const blogIndex = latestBlogs.findIndex(
                    (b) => getBlogId(b) === getBlogId(matched)
                );
                if (blogIndex !== -1) {
                    setPage(Math.floor(blogIndex / pageSize) + 1);
                }
            }
        } else {
            setSelectedBlog((prev) => (prev ? null : prev));
        }
    }, [location.search, routeBlogId, activeBlogList, latestBlogs, pageSize]);

    const openBlog = (blog) => {
        if (!blog) return;
        setSelectedBlog(blog);
        const id = getBlogId(blog);
        if (id) {
            navigate(
                { pathname: location.pathname, search: `?blog=${encodeURIComponent(id)}` },
                { replace: true }
            );
        }
    };

    const closeBlog = () => {
        setSelectedBlog(null);
        navigate({ pathname: location.pathname, search: "" }, { replace: true });
    };

    const handleCopyLink = async (e, blog) => {
        if (e) {
            if (typeof e.preventDefault === "function") e.preventDefault();
            if (typeof e.stopPropagation === "function") e.stopPropagation();
        }
        if (!blog) return;

        const url = getBlogShareUrl(blog);
        const id = getBlogId(blog);

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = url;
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }
            setCopiedBlogId(id);
            setTimeout(() => {
                setCopiedBlogId((current) => (current === id ? null : current));
            }, 2500);
        } catch (err) {
            console.error("Failed to copy blog link:", err);
        }
    };

    return (
        <section
            id="resources"
            ref={ref}
            data-testid="resources-section"
            className="py-20 md:py-24 bg-muted/30 border-y scroll-mt-24"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-12 md:mb-14">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-5">
                            <Newspaper className="w-4 h-4" />
                            Blog & Resources
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">
                            Helpful recovery content, not content for content&apos;s sake.
                        </h2>
                        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                            Practical writing on cravings, check-ins, journaling, and what works between
                            meetings — written for people in recovery, by people in recovery.
                        </p>
                    </div>
                </div>

                <div
                    className={`grid ${
                        isLoading || guideBlog || supportBlog
                            ? "lg:grid-cols-[1.15fr_0.85fr]"
                            : "grid-cols-1"
                    } gap-6`}
                >
                    {isLoading ? (
                        <div className="grid md:grid-cols-2 gap-6">
                            {[1, 2].map((n) => (
                                <div
                                    key={n}
                                    className="rounded-3xl border bg-card p-7 animate-pulse min-h-[20rem] flex flex-col justify-between"
                                >
                                    <div className="space-y-4">
                                        <div className="w-12 h-12 rounded-2xl bg-muted" />
                                        <div className="h-4 w-20 bg-muted rounded-full" />
                                        <div className="h-7 w-3/4 bg-muted rounded" />
                                        <div className="h-4 w-full bg-muted rounded" />
                                        <div className="h-4 w-5/6 bg-muted rounded" />
                                    </div>
                                    <div className="h-5 w-24 bg-muted rounded mt-6" />
                                </div>
                            ))}
                        </div>
                    ) : (guideBlog || supportBlog) ? (
                        <div
                            className={`grid ${
                                guideBlog && supportBlog
                                    ? "md:grid-cols-2"
                                    : "grid-cols-1"
                            } gap-6`}
                        >
                            {/* 1. GUIDE CARD */}
                            {guideBlog && (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openBlog(guideBlog)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            openBlog(guideBlog);
                                        }
                                    }}
                                    data-testid="resource-card-guide"
                                    className="group cursor-pointer rounded-3xl border bg-card p-7 hover:shadow-lg hover:border-primary/30 hover:-translate-y-1 transition-all flex flex-col justify-between text-left"
                                >
                                    <div>
                                        <div className="flex items-center justify-between mb-5">
                                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700">
                                                <BookOpenText className="w-6 h-6" />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => handleCopyLink(e, guideBlog)}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                                    copiedBlogId === getBlogId(guideBlog)
                                                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                                        : "border-slate-200 bg-white/90 hover:bg-slate-100 text-slate-700 hover:text-slate-900 shadow-sm"
                                                }`}
                                                title="Copy link to blog"
                                            >
                                                {copiedBlogId === getBlogId(guideBlog) ? (
                                                    <>
                                                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                        <span>Link copied</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Link2 className="w-3.5 h-3.5" />
                                                        <span>Copy link</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">
                                            {guideBlog.category || "Guide"}
                                        </div>
                                        <h3 className="text-2xl font-bold text-foreground tracking-tight leading-tight line-clamp-2">
                                            {guideBlog.title}
                                        </h3>
                                        <p className="mt-4 text-muted-foreground leading-relaxed line-clamp-3">
                                            {guideBlog.excerpt || guideBlog.details}
                                        </p>
                                    </div>
                                    <div className="mt-6 inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                                        Read More
                                        <ArrowRight className="w-4 h-4" />
                                    </div>
                                </div>
                            )}

                            {/* 2. SUPPORT CARD */}
                            {supportBlog && (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openBlog(supportBlog)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            openBlog(supportBlog);
                                        }
                                    }}
                                    data-testid="resource-card-support"
                                    className="group cursor-pointer rounded-3xl border bg-card p-7 hover:shadow-lg hover:border-primary/30 hover:-translate-y-1 transition-all flex flex-col justify-between text-left"
                                >
                                    <div>
                                        <div className="flex items-center justify-between mb-5">
                                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-rose-100 text-rose-700">
                                                <HeartPulse className="w-6 h-6" />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => handleCopyLink(e, supportBlog)}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                                    copiedBlogId === getBlogId(supportBlog)
                                                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                                        : "border-slate-200 bg-white/90 hover:bg-slate-100 text-slate-700 hover:text-slate-900 shadow-sm"
                                                }`}
                                                title="Copy link to blog"
                                            >
                                                {copiedBlogId === getBlogId(supportBlog) ? (
                                                    <>
                                                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                        <span>Link copied</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Link2 className="w-3.5 h-3.5" />
                                                        <span>Copy link</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-3">
                                            {supportBlog.category || "Support"}
                                        </div>
                                        <h3 className="text-2xl font-bold text-foreground tracking-tight leading-tight line-clamp-2">
                                            {supportBlog.title}
                                        </h3>
                                        <p className="mt-4 text-muted-foreground leading-relaxed line-clamp-3">
                                            {supportBlog.excerpt || supportBlog.details}
                                        </p>
                                    </div>
                                    <div className="mt-6 inline-flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                                        Read More
                                        <ArrowRight className="w-4 h-4" />
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}

                    {/* 3. FIND SUPPORT / DIRECTORY FEATURE CARD */}
                    <a
                        href="/directory"
                        data-testid="resource-feature-card"
                        className={`group relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950/60 text-white p-8 md:p-10 min-h-[22rem] flex flex-col justify-end hover:-translate-y-1 transition-transform shadow-xl ${
                            !guideBlog && !supportBlog ? "max-w-3xl" : ""
                        }`}
                    >
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-500/10 via-transparent to-transparent pointer-events-none" />
                        <div className="relative z-10">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-xs uppercase tracking-[0.18em] text-emerald-300 font-semibold mb-4">
                                <Compass className="w-4 h-4" />
                                Find support
                            </div>
                            <h3 className="text-3xl font-bold tracking-tight leading-tight max-w-sm">
                                Private, judgment-free search when you&apos;re ready for more support.
                            </h3>
                            <p className="mt-4 text-white/75 max-w-md leading-relaxed">
                                When you&apos;re ready for in-person care, our directory connects you with
                                verified sober living, rehabs, and outpatient programs — privately, no
                                account required.
                            </p>
                            <div className="mt-6 inline-flex items-center gap-2 text-white font-semibold group-hover:gap-3 transition-all">
                                Browse the directory
                                <ArrowRight className="w-4 h-4" />
                            </div>
                        </div>
                    </a>
                </div>

                {/* 4. LATEST BLOG POSTS */}
                <div className="mt-14 md:mt-16">
                    <div className="flex items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3">
                            <h3 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                                Latest blog posts
                            </h3>
                            {isLoading && (
                                <Loader2 className="w-5 h-5 text-primary animate-spin" />
                            )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                            Showing {paginatedBlogs.length} of {latestBlogs.length} blogs
                        </p>
                    </div>

                    {isLoading && latestBlogs.length === 0 ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {[1, 2, 3].map((n) => (
                                <div
                                    key={n}
                                    className="rounded-2xl border bg-card p-5 animate-pulse min-h-[180px] flex flex-col justify-between"
                                >
                                    <div className="space-y-3">
                                        <div className="h-5 w-24 bg-muted rounded-full" />
                                        <div className="h-6 w-3/4 bg-muted rounded" />
                                        <div className="h-4 w-full bg-muted rounded" />
                                    </div>
                                    <div className="h-4 w-20 bg-muted rounded mt-4" />
                                </div>
                            ))}
                        </div>
                    ) : latestBlogs.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-border p-12 text-center bg-card/50">
                            <Newspaper className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-60" />
                            <p className="text-base font-semibold text-foreground">
                                No additional blog posts yet
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Check back soon for more recovery guides and stories.
                            </p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {paginatedBlogs.map((blog) => (
                                <article
                                    key={blog.id || blog.title}
                                    className="rounded-2xl border bg-card p-5 hover:shadow-md transition-shadow flex flex-col justify-between"
                                >
                                    <div>
                                        <div className="flex items-center gap-2 text-xs mb-3">
                                            <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                                                {blog.category || "General"}
                                            </span>
                                            <span className="text-muted-foreground">
                                                {formatBlogDate(blog)}
                                            </span>
                                        </div>
                                        <h4 className="text-lg font-bold text-foreground leading-snug line-clamp-2">
                                            {blog.title}
                                        </h4>
                                        <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                                            {blog.excerpt || blog.details}
                                        </p>
                                    </div>
                                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                                        <button
                                            type="button"
                                            onClick={() => openBlog(blog)}
                                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all self-start"
                                        >
                                            Read More
                                            <ArrowRight className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => handleCopyLink(e, blog)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                                copiedBlogId === getBlogId(blog)
                                                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                                    : "border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700"
                                            }`}
                                            title="Copy link to blog"
                                        >
                                            {copiedBlogId === getBlogId(blog) ? (
                                                <>
                                                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                    <span>Link copied</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Link2 className="w-3.5 h-3.5 text-slate-500" />
                                                    <span>Copy link</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                disabled={page === 1}
                                className="h-10 px-4 rounded-full border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/40 transition-colors"
                            >
                                Previous
                            </button>
                            {Array.from({ length: totalPages }).map((_, index) => {
                                const pageNumber = index + 1;
                                const isActive = pageNumber === page;
                                return (
                                    <button
                                        type="button"
                                        key={pageNumber}
                                        onClick={() => setPage(pageNumber)}
                                        className={`h-10 w-10 rounded-full text-sm font-semibold transition-colors ${
                                            isActive
                                                ? "bg-slate-950 text-white"
                                                : "border hover:bg-muted/40"
                                        }`}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            })}
                            <button
                                type="button"
                                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                disabled={page === totalPages}
                                className="h-10 px-4 rounded-full border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/40 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* BLOG DETAILS MODAL */}
            {selectedBlog && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                    <button
                        type="button"
                        aria-label="Close popup"
                        className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm"
                        onClick={closeBlog}
                    />
                    <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-[2rem] border border-slate-200 bg-white shadow-2xl">
                        {selectedBlog.image ? (
                            <div className="relative h-64 md:h-72 shrink-0">
                                <img
                                    src={selectedBlog.image}
                                    alt={selectedBlog.title}
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 via-slate-950/5 to-transparent" />
                                <div className="absolute top-4 left-4 rounded-md bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-700">
                                    {selectedBlog.category || "General"}
                                </div>
                            </div>
                        ) : null}

                        {/* Modal Action Controls: Copy Link & Close */}
                        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={(e) => handleCopyLink(e, selectedBlog)}
                                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-xs font-semibold shadow-md backdrop-blur-sm transition-all ${
                                    copiedBlogId === getBlogId(selectedBlog)
                                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                        : "border-slate-300 bg-white/95 text-slate-700 hover:text-slate-900 hover:bg-white"
                                }`}
                                title="Copy link to blog"
                            >
                                {copiedBlogId === getBlogId(selectedBlog) ? (
                                    <>
                                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                                        <span>Link copied</span>
                                    </>
                                ) : (
                                    <>
                                        <Link2 className="w-3.5 h-3.5 text-slate-600" />
                                        <span>Copy link</span>
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={closeBlog}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white/95 backdrop-blur-sm text-slate-600 hover:text-slate-900 shadow-md transition-colors"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className={`p-6 md:p-8 ${!selectedBlog.image ? "pt-12 md:pt-14" : ""}`}>
                            {!selectedBlog.image && (
                                <div className="inline-block rounded-md bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-700 mb-4">
                                    {selectedBlog.category || "General"}
                                </div>
                            )}
                            <h3 className="text-3xl font-bold tracking-tight text-foreground pr-32">
                                {selectedBlog.title}
                            </h3>
                            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-600">
                                <span className="inline-flex items-center gap-1.5">
                                    <UserRound className="h-4 w-4" />
                                    {selectedBlog.author || "Genesis Team"}
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <CalendarDays className="h-4 w-4" />
                                    {formatBlogDate(selectedBlog)}
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <Clock3 className="h-4 w-4" />
                                    {selectedBlog.readTime || "5 min read"}
                                </span>
                            </div>
                            {selectedBlog.excerpt && (
                                <p className="mt-5 text-lg font-medium text-slate-800 leading-relaxed">
                                    {selectedBlog.excerpt}
                                </p>
                            )}
                            <div className="mt-4 text-[15px] md:text-base text-slate-700 leading-relaxed whitespace-pre-line">
                                {selectedBlog.details || selectedBlog.excerpt}
                            </div>
                            {selectedBlog.tags && (
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {(Array.isArray(selectedBlog.tags)
                                        ? selectedBlog.tags
                                        : typeof selectedBlog.tags === "string"
                                        ? selectedBlog.tags
                                              .split(",")
                                              .map((t) => t.trim())
                                              .filter(Boolean)
                                        : []
                                    ).map((tag) => (
                                        <span
                                            key={tag}
                                            className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="mt-6 border-t pt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
                                <span>
                                    Informational only. Recovery journeys are personal - seek professional
                                    support when needed.
                                </span>
                                <button
                                    type="button"
                                    onClick={(e) => handleCopyLink(e, selectedBlog)}
                                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                        copiedBlogId === getBlogId(selectedBlog)
                                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                            : "border-slate-300 bg-slate-100 hover:bg-slate-200 text-slate-700"
                                    }`}
                                >
                                    {copiedBlogId === getBlogId(selectedBlog) ? (
                                        <>
                                            <Check className="w-3.5 h-3.5 text-emerald-600" />
                                            <span>Link copied</span>
                                        </>
                                    ) : (
                                        <>
                                            <Link2 className="w-3.5 h-3.5" />
                                            <span>Copy blog link</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
